import React, { useState, useEffect, useCallback } from 'react';
//MUI
import { DataGrid, GridToolbar } from '@mui/x-data-grid';
import Stack from '@mui/material/Stack';
import Divider from '@mui/material/Divider';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Modal from '@mui/material/Modal';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import NoteAdd from '@mui/icons-material/NoteAdd';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import Select from '@mui/material/Select';
import InputAdornment from '@mui/material/InputAdornment';
import CloseIcon from '@mui/icons-material/Close';
import IconButton from '@mui/material/IconButton';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import { showToast } from '../resources/utils/toast';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import { useTheme } from '@mui/material/styles';

//Custom
import {APPROVAL_OPTIONS, FIVE_DAY_COLUMNS} from '../resources/constants/FiveDayTableConstants';
import Comments from './Comments';
import BalanceDetails from './BalanceDetails';
import API_CONSTANTS from '../resources/constants/api.json';

const assignTo = "fran@vantagepointpropertygroup.com"


export default function FiveDayTable(props) {
    const theme = useTheme();
    const standardNoticeFeeCode = 817341
    const certifiedNoticeFeeCode = -817341

    const [funcArgs, setFuncArgs] = useState(false);
    const [modalOpen, setModalOpen] = useState(false);
    const [dnsReason, setDNSReason] = useState(false);
    const [selectedRow, setSelectedRow] = useState(null);
    const [approval, setApproval] = useState("not_set");
    const [editingId, setEditingId] = useState(null);
    const [isUpdating, setIsUpdating] = useState(false);
    const [loading, setLoading] = useState(true);
    const [standardNoticeFee, setStandardNoticeFee] = useState(null);
    const [certifiedNoticeFee, setCertifiedNoticeFee] = useState(null);
    const [standardFeeError, setStandardFeeError] = useState(null);
    const [certifiedFeeError, setCertifiedFeeError] = useState(null);
    const [certifiedNoticeFeeSuccess, setCertifiedNoticeFeeSuccess] = useState(false);
    const [standardNoticeFeeSuccess, setStandardNoticeFeeSuccess] = useState(false);
    const [noticeFeeSuccess, setNoticeFeeSuccess] = useState(false);
    const [isPaneOpen, setIsPaneOpen] = useState(false);
    const [toastOpen, setToastOpen] = useState(false);
    const [toastMessage, setToastMessage] = useState('');
    const [toastSeverity, setToastSeverity] = useState('error');

    const rowJson = props.rowJson;
    const setRowJson = props.setRowJson;

    useEffect(() => {
        fetch(`${API_CONSTANTS.API_ROUTE}/fiveDayNotices?run_date=${props.runDate}`)
            .then((response) => response.json())
            .then(async (notices) => {
                const commentsResponse = await fetch(`${API_CONSTANTS.API_ROUTE}/loadAllComments`);
                var allComments = await commentsResponse.json();
                if (!allComments.length){
                    console.error(allComments.error)
                    allComments = []
                }
                const commentsByLease = allComments.reduce((acc, comment) => {
                    const leaseId = comment.lease_id;
                    if (!acc[leaseId]) {
                        acc[leaseId] = [];
                    }
                    acc[leaseId].push(comment);
                    return acc;
                }, {});
                const rowsWithComments = notices.map(row => ({
                    ...row,
                    comments: (commentsByLease[row.Accountnumber] || [])
                        .sort((a, b) => new Date(b.create_dttm) - new Date(a.create_dttm))
                }));

                setRowJson(rowsWithComments);
                setLoading(false);
            });
    }, [setRowJson, props.runDate]);

    const updateComments = useCallback((leaseId, newComments) => {
        setRowJson(prevRowJson => {
            return prevRowJson.map(row => {
                if (row.Accountnumber === leaseId) {
                    return {
                        ...row,
                        comments: newComments.sort((a, b) => 
                            new Date(b.create_dttm) - new Date(a.create_dttm)
                        )
                    };
                }
                return row;
            });
        });
    }, [setRowJson]);

    useEffect(()=>{
        if (selectedRow) {
            setApproval(selectedRow.status_text)
            setStandardNoticeFee(selectedRow.standardFeeAmount);
            setCertifiedNoticeFee(selectedRow.certifiedFeeAmount);
        }
    },[selectedRow])

    useEffect(()=>{
        if (!modalOpen && selectedRow){
            let rows = rowJson.map(r => {
                if (r.Accountnumber === selectedRow.Accountnumber){
                    if (standardNoticeFee){
                        r.standardFeeAmount = standardNoticeFee
                    }
                    if (certifiedNoticeFee){
                        r.certifiedFeeAmount = certifiedNoticeFee
                    }
                }
                return r
            })
            setRowJson(rows)
            setStandardFeeError();
            setCertifiedFeeError();
            setStandardNoticeFeeSuccess(false);
            setCertifiedNoticeFeeSuccess(false);
        }
    },[modalOpen])

    const openModal = (modalType, funcArgs) => {
        setFuncArgs(funcArgs)
        setModalOpen(true)
    }

    const sendNoticeFee = (fee, feeType) => {
        fetch(`${API_CONSTANTS.API_ROUTE}/addNoticeFee`,{
            method: 'POST',
            headers: { 'Content-Type': 'application/json'},
            body: JSON.stringify({
                leaseId: selectedRow.Accountnumber,
                user: props.userInfo._id,
                feeAmount: fee,
                feeType: feeType,
                run_date: props.runDate
            })
            }).then((res)=>{
                if (feeType === standardNoticeFeeCode){
                    setStandardNoticeFeeSuccess(true)
                }
                else{
                    setCertifiedNoticeFeeSuccess(true)
                }
            })
    }

    const setDNS = () => {
        let doNotServe  = selectedRow.DoNotServe ? 0 : 1
        let newRowJson = rowJson.map((row)=>{
            if(row.id === selectedRow.id){
                row.DoNotServe = doNotServe
            }
            return row
        })
        setRowJson(newRowJson)
        fetch(`${API_CONSTANTS.API_ROUTE}/setDNS`,{
            method: 'POST',
            headers: { 'Content-Type': 'application/json'},
            body: JSON.stringify({
                leaseId: selectedRow.Accountnumber,
                userId: props.userInfo._id,
                run_date: props.runDate,
                doNotServe: doNotServe,
                dnsReason: dnsReason
            })
            }).then((res)=>{
                console.log(res)
            })
    }


    const updateApprovalStatus = async (row, status) => {
        setIsUpdating(true);
        try {
            const response = await fetch(
                `${API_CONSTANTS.API_ROUTE}/updateApprovalStatus?user_id=${props.userInfo._id}&run_date=${props.runDate}&_id=${row.id}&status=${status}&lease_id=${row.Accountnumber}`,
                { method: 'POST' }
            );
            
            if (response.status === 200) {
                const updatedRowJson = rowJson.map(r =>
                    r.Accountnumber === row.Accountnumber
                        ? { ...r, status_text: status }
                        : r
                );
                setRowJson(updatedRowJson);
                setEditingId(null);
    
                if (status === "certified") {
                    try {
                        let assignmentEmail = assignTo;
                        let assignmentResponse = await fetch(`${API_CONSTANTS.API_ROUTE}/updateAssignment`, {
                            method: "POST",
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify({
                                obid: row.id,
                                run_date: props.runDate,
                                user_id: props.userInfo._id,
                                email: assignmentEmail,
                                lease_id: row.Accountnumber
                            })
                        });
    
                        if (assignmentResponse.ok) {
                            showToast(setToastMessage, setToastSeverity, setToastOpen, `Assignment to ${assignTo} successful`, 'success');
                            return { ok: true };
                        } else {
                            showToast(setToastMessage, setToastSeverity, setToastOpen, 'Assignment failed. Please try to assign in Dashboard', 'warning');
                            return { ok: false, error: 'Assignment failed' };
                        }
                    } catch (assignmentError) {
                        console.error('Error updating assignment:', assignmentError);
                        showToast(setToastMessage, setToastSeverity, setToastOpen, 'Error updating assignment', 'error');
                        return { ok: false, error: assignmentError };
                    }
                }
                return { ok: true };
            }
    
            showToast(setToastMessage, setToastSeverity, setToastOpen, 'Failed to update status', 'error');
            return { ok: false };
        } catch (error) {
            console.error('Error updating status:', error);
            showToast(setToastMessage, setToastSeverity, setToastOpen, 'Error updating status', 'error');
            return { ok: false, error };
        } finally {
            setIsUpdating(false);
        }
    };

    const dataGridWidth = isPaneOpen ? '75%' : '100%';
    const paneWidth = isPaneOpen ? '25%' : '0%';

    return (

        <Box sx={{ bgcolor: theme.palette.background.default, height: '85vh', width: '100%', display: 'flex', flexDirection: 'row'}}>
            <Modal
                open={modalOpen}
                onClose={()=>{setModalOpen(false);}}
                aria-labelledby="otp-modal"
                aria-describedby="modal-for-otp"
            >
                <Box sx={ {
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    bgcolor: theme.palette.background.paper,
                    border: '2px solid',
                    borderColor: theme.palette.divider,
                    borderRadius: 2,
                    boxShadow: 24,
                    margin: "0",
                    padding: 7,
                    textAlign: "center",
                    width: "50%",
                    maxHeight: "85vh",
                    overflow: 'auto'
                }}>
                    <IconButton
                        aria-label="close"
                        onClick={() => setModalOpen(false)}
                        sx={{
                            position: 'absolute',
                            right: 8,
                            top: 8,
                            color: theme.palette.text.secondary,
                        }}
                    >
                        <CloseIcon />
                    </IconButton>

                        <Typography id='modal-modal-title' variant="h5" component="h2">{(selectedRow)?selectedRow.Tenants:''}</Typography>
                        <Typography id='modal-modal-description'  >{(selectedRow)?selectedRow.Unit:''}</Typography>
                        <Divider variant='middle' sx={{marginBottom:1}}/>

                    <Stack width={"100%"} spacing={5}>

                            <Stack direction="row" divider={<Divider orientation="vertical" flexItem/>}>                               
                            <FormControl variant="standard" sx={{ m: 1, minWidth: 120 }}>
                                <Select
                                    id="approval_select"
                                    value={approval || 'not_set'}
                                    defaultValue="not_set"
                                    onChange={(ev)=>{setApproval(ev.target.value)}}
                                >
                                    {APPROVAL_OPTIONS.map(option => (
                                        (props.userInfo.can_approve === 1) && (
                                            <MenuItem key={option.value} value={option.value}>
                                                {option.label}
                                            </MenuItem>
                                        )
                                    ))}
                                </Select>
                            </FormControl>
                                <Button  variant={"contained"}
                                    onClick={() => {
                                        updateApprovalStatus(selectedRow, approval)}}>
                                    Set Approval
                                </Button>
                        </Stack>


                        <Box width={"100%"} alignItems={"center"}
                            sx={{
                                paddingTop: 1, marginTop: 1,
                                '& .MuiTypography-root': {
                                    marginBottom: 1,
                                },
                                '&.MuiBox-root': {
                                    marginTop: '20px !important'
                                }
                            }}>
                            <Typography align="left" sx={{fontWeight:"bold"}}>
                                Comments:
                            </Typography>
                            <Comments
                                lease_id={funcArgs.lease_id}
                                run_date={props.runDate}
                                user_info={props.userInfo}
                                isPaneOpen={isPaneOpen}
                                updateComments={updateComments}
                            />
                        </Box>
                        <Box width={"100%"} alignItems={"center"}
                            sx={{
                                paddingTop: 1, marginTop: 1,
                                '& .MuiTypography-root': {
                                    marginBottom: 1,
                                },
                                '&.MuiBox-root': {
                                    marginTop: '20px !important'
                                }
                            }}>
                            <Typography align="left" sx={{fontWeight:"bold"}}>
                                Do Not Serve:
                            </Typography>
                            <Stack direction={"row"} width="100%" justifyItems={"end"}>
                                <TextField
                                    fullWidth
                                    id="dns-reason"
                                    placeholder="DNS Reason"
                                    variant="standard"
                                        onChange={(event)=>{setDNSReason(event.target.value)}}
                                />
                                <Button
                                    disabled={dnsReason < 3}
                                        onClick={()=>{
                                            setDNS()}}>
                                    <NoteAdd fontSize={"large"} />
                                </Button>
                            </Stack>
                        </Box>
                        <Box>
                            <Typography
                                justifySelf={'flex-start'}
                                sx={
                                    standardFeeError || certifiedFeeError ? 
                                    { color: "red", fontWeight:"bold"} : 
                                    {color: "black", fontWeight:"bold"}
                                }
                            >
                                Notice Fee:
                            </Typography>
                            <Stack direction="row" justifyContent={"space-evenly"} alignItems={'center'}>
                                <TextField
                                    // disabled={!noticeFeeType}
                                    error={standardFeeError}
                                    helperText={standardFeeError}
                                    id="standardNoticeFeeInput"
                                    startAdornment={<InputAdornment position="start">$</InputAdornment>}
                                    label="Standard Fee Amount"
                                    variant="standard"
                                    value={standardNoticeFee}
                                    size='small'
                                    defaultValue="USD"
                                    sx={{
                                        marginBottom: 2  // or use mb: 2
                                    }}
                                        onChange={(ev)=>{
                                            //value is a number
                                            if (standardNoticeFeeSuccess) {setStandardNoticeFeeSuccess(false);}

                                            if (!isNaN(ev.target.value)){
                                                setStandardNoticeFee(ev.target.value)
                                            }
                                            else{
                                                setStandardFeeError('The notice fee must be a number')
                                            }
                                        }}
                                        onBlur={()=>{
                                            if (!standardNoticeFee){
                                                setStandardFeeError('The notice fee must be a number')
                                            }
                                            else if (isNaN(standardNoticeFee)){
                                                setStandardFeeError('The notice fee must be a number')
                                            }
                                            else{
                                                let fee = parseFloat(standardNoticeFee).toFixed(2);
                                                setStandardNoticeFee(fee);
                                                setStandardFeeError(null)
                                                sendNoticeFee(fee, standardNoticeFeeCode)
                                            }
                                        }}
                                />
                                <div style={{width:10}}>
                                    <CheckCircleOutlineIcon 
                                        fontSize='small' 
                                        sx={{
                                            color:"green", 
                                            display: standardNoticeFeeSuccess ? 
                                                "inline" : 
                                                "none"}} />
                                </div>
                                <Divider orientation="vertical" flexItem />
                                <TextField
                                    // disabled={!noticeFeeType}
                                    error={certifiedFeeError}
                                    helperText={certifiedFeeError}
                                    id="certifiedNoticeFeeInput"
                                    startAdornment={<InputAdornment position="start">$</InputAdornment>}
                                    label="Certified Fee Amount"
                                    variant="standard"
                                    value={certifiedNoticeFee}
                                    size='small'
                                    defaultValue="USD"
                                    sx={{
                                        marginBottom: 2  // or use mb: 2
                                    }}
                                        onChange={(ev)=>{
                                            //value is a number
                                            if (certifiedNoticeFeeSuccess) {setCertifiedNoticeFeeSuccess(false);}

                                            if (!isNaN(ev.target.value)){
                                            setCertifiedNoticeFee(ev.target.value)
                                        }
                                            else{
                                            setCertifiedFeeError('The notice fee must be a number')
                                        }
                                    }}
                                        onBlur={()=>{
                                            if (!certifiedNoticeFee){
                                                setCertifiedFeeError('The notice fee must be a number')
                                            }
                                            else if (isNaN(certifiedNoticeFee)){
                                            setCertifiedFeeError('The notice fee must be a number')
                                        }
                                            else{
                                            let fee = parseFloat(certifiedNoticeFee).toFixed(2);
                                            setCertifiedNoticeFee(fee);
                                            setCertifiedFeeError(null)
                                            sendNoticeFee(fee,certifiedNoticeFeeCode)
                                        }
                                    }}
                                />
                                <div style={{width:10}}>
                                <CheckCircleOutlineIcon 
                                    fontSize='small' 
                                    sx={{
                                        color:"green", 
                                        display: certifiedNoticeFeeSuccess ? 
                                            "inline" : 
                                            "none"}} />
                                </div>
                            </Stack>
                                <Divider variant='middle' sx={{marginBottom:1, marginTop:1}}/>
                            <Typography sx={{fontWeight:"bold"}}>Balance Details</Typography>
                            <BalanceDetails
                                run_date={props.runDate}
                                funcArgs={funcArgs}
                            />
                        </Box>
                        <Button
                            variant={"contained"}
                                onClick={()=>{window.open(`https://vantagepointproperties.managebuilding.com/Manager/app/rentroll/${selectedRow.Accountnumber}/summary`, '_blank').focus();}}> Open Lease </Button>
                    </Stack>
                </Box>
            </Modal>

            <Box width={dataGridWidth} display={'flex'} flexDirection={'column'}>
                <Box>
                    <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>Five Day Notice Data</Typography>
                </Box>
            {/* <Box>
                Filters and search go here
            </Box> */}
                <Box display={'flex'} flexDirection={'column'} width="100%">
                    <DataGrid
                    sx={(theme) => ({
                        fontSize:12,
                        '& .MuiDataGrid-row': {
                            borderBottom: '1px solid',
                            borderColor: theme.palette.divider,
                            '&:hover': {
                                backgroundColor: theme.palette.action.hover,
                            },
                        },
                        '& .MuiDataGrid-cell': {
                            borderBottom: '1px solid',
                            borderColor: theme.palette.divider,
                        },
                        backgroundColor: theme.palette.background.paper,
                    })}
                        rows={rowJson}
                        loading={loading}
                        slots={{ toolbar: GridToolbar }}
                        slotProps={{
                            loadingOverlay: {
                                variant: 'linear-progress',
                                noRowsVariant: 'linear-progress',
                            },
                            toolbar: {
                                showQuickFilter: true,
                            },
                        }}
                    getRowID={(row)=>row.Accountnumber}
                        columns={FIVE_DAY_COLUMNS(
                            openModal,
                            setSelectedRow,
                            updateApprovalStatus,
                            props.userInfo,
                            editingId,
                            setEditingId,
                            setToastMessage,
                            setToastSeverity,
                            setToastOpen

                        )}
                        initialState={{
                            pagination: {
                                paginationModel: {
                                    pageSize: 10,
                                },
                            },
                        }}
                        pageSizeOptions={[5, 10]}
                        onRowClick={(params) => {
                            setSelectedRow(params.row);
                            setIsPaneOpen(true);
                        }}
                    />
                </Box>
            </Box>
            <Box width={paneWidth} sx={{ bgcolor: theme.palette.background.paper, borderLeft: '1px solid', borderLeftColor: theme.palette.divider, overflow: 'hidden' }}>
                {isPaneOpen && selectedRow && (
                    <Box p={2}>
                        <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                            <Typography variant="h6">Comments for {selectedRow.Unit}</Typography>
                            <IconButton onClick={() => setIsPaneOpen(false)} aria-label="close pane">
                                <CloseIcon />
                            </IconButton>
                        </Box>
                        <Comments
                            isPaneOpen={isPaneOpen}
                            lease_id={selectedRow.Accountnumber}
                            run_date={props.runDate}
                            user_info={props.userInfo}
                            updateComments={updateComments}
                        />
                    </Box>
                )}
            </Box>
            <Snackbar 
                open={toastOpen} 
                autoHideDuration={6000} 
                onClose={() => setToastOpen(false)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
                <Alert 
                    onClose={() => setToastOpen(false)} 
                    severity={toastSeverity}
                    sx={{ width: '100%' }}
                >
                    {toastMessage}
                </Alert>
            </Snackbar>
        </Box>
    );
}